import React, { useState, useEffect } from "react";
import { Table, Button, Modal, Form, Input, Select, message, Space, Popconfirm } from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { apiClient } from "../../utils/apiClient";
import { dynamicCommitteesApi } from "../../config/api";
import "./about-college.scss"; // Assuming this exists or create a new one

const ManageCommittees = () => {
  const [committees, setCommittees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form] = Form.useForm();

  const fetchCommittees = async () => {
    setLoading(true);
    try {
      const response = await apiClient(`${dynamicCommitteesApi}/committees`);
      if (!response.ok) throw new Error("Failed to fetch committees");
      const result = await response.json();
      setCommittees(result.data || []);
    } catch (error) {
      console.error(error);
      message.error("Error loading committees");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCommittees();
  }, []);

  const handleOpenModal = (record = null) => {
    if (record) {
      setEditingId(record._id);
      form.setFieldsValue({
        name: record.name,
        description: record.description,
        status: record.status,
      });
    } else {
      setEditingId(null);
      form.resetFields();
      form.setFieldsValue({ status: "Active" });
    }
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    form.resetFields();
    setEditingId(null);
  };

  const handleSubmit = async (values) => {
    try {
      const url = editingId
        ? `${dynamicCommitteesApi}/committees/${editingId}`
        : `${dynamicCommitteesApi}/committees`;
      const method = editingId ? "PUT" : "POST";

      const response = await apiClient(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || "Failed to save committee");
      }

      message.success(`Committee ${editingId ? "updated" : "created"} successfully`);
      fetchCommittees();
      handleCloseModal();
    } catch (error) {
      message.error(error.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await apiClient(`${dynamicCommitteesApi}/committees/${id}`, {
        method: "DELETE",
      });
      if (!response.ok) throw new Error("Failed to delete committee");
      message.success("Committee deleted successfully");
      fetchCommittees();
    } catch (error) {
      message.error(error.message);
    }
  };

  const columns = [
    {
      title: "Committee Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <span style={{ color: status === "Active" ? "green" : "red" }}>{status}</span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleOpenModal(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this committee?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="primary" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="manage-committees-container" style={{ padding: "20px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
        <h2>Manage Dynamic Committees</h2>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => handleOpenModal()}
        >
          Add New Committee
        </Button>
      </div>

      <Table
        dataSource={committees}
        columns={columns}
        rowKey="_id"
        loading={loading}
        bordered
      />

      <Modal
        title={editingId ? "Edit Committee" : "Add New Committee"}
        open={isModalVisible}
        onCancel={handleCloseModal}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label="Committee Name"
            rules={[{ required: true, message: "Please enter the committee name" }]}
          >
            <Input placeholder="e.g. Cultural Committee" />
          </Form.Item>
          
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="Brief description..." />
          </Form.Item>

          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true }]}
          >
            <Select>
              <Select.Option value="Active">Active</Select.Option>
              <Select.Option value="Inactive">Inactive</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item style={{ textAlign: "right", marginBottom: 0 }}>
            <Space>
              <Button onClick={handleCloseModal}>Cancel</Button>
              <Button type="primary" htmlType="submit">
                {editingId ? "Update" : "Create"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ManageCommittees;
